import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { LoanOfficerSelect } from './LoanOfficerSelect';
import { Loader2 } from 'lucide-react';

interface BulkAssignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedContacts: string[];
  contactNames?: string[];
  onAssign: (officerId: string) => Promise<void>;
}

export function BulkAssignDialog({
  open,
  onOpenChange,
  selectedContacts,
  contactNames = [],
  onAssign
}: BulkAssignDialogProps) {
  const [selectedOfficerId, setSelectedOfficerId] = useState<string>('');
  const [assigning, setAssigning] = useState(false);

  const handleAssign = async () => {
    if (!selectedOfficerId) {
      return;
}


    setAssigning(true);
    try {
      await onAssign(selectedOfficerId);
      onOpenChange(false);
      setSelectedOfficerId('');
    } catch (error) {
      console.error('BulkAssignDialog: Failed to assign contacts:', error);
      // Don't close dialog on error so user can try again
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign Loan Officer</DialogTitle>
          <DialogDescription>
            Assign {selectedContacts.length} selected contact{selectedContacts.length !== 1 ? 's' : ''} to a loan officer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {contactNames.length > 0 && (
            <div className="text-sm text-muted-foreground">
              <p className="font-medium mb-1">Selected contacts:</p>
              <ul className="list-disc list-inside max-h-32 overflow-y-auto">
                {contactNames.slice(0, 10).map((name, index) => (
                  <li key={index}>{name}</li>
                ))}
                {contactNames.length > 10 && (
                  <li className="italic">and {contactNames.length - 10} more...</li>
                )}
              </ul>
            </div>
          )}

          <LoanOfficerSelect
            value={selectedOfficerId}
            onChange={setSelectedOfficerId}
            label="Select Loan Officer"
            placeholder="Choose a loan officer"
            includeUnassigned={false}
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={assigning}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={!selectedOfficerId || assigning}
          >
            {assigning ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Assigning...
              </>
            ) : (
              'Assign'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}